import { mediaUrl } from '@bluheadless/ui-logic/src/assets'
import { IMAGE_DEFAULT_LOADING, IMAGE_DEFAULT_PRIORITY, IMAGE_LOADING_EAGER } from '@bluheadless/ui-logic/src/constants'
import { normalizeCarriageReturns } from '@bluheadless/ui-logic/src/utils/string'
import { isFullURL } from '@bluheadless/ui-logic/src/utils/url'
import { css } from '@mui/system'
import { attributesToProps } from 'html-react-parser'
import unescape from 'lodash/unescape'

const parseJsonValue = (value) => {
	if (!value) {
		return null
	}
	try {
		const parsed = JSON.parse(value)
		return parsed
	} catch (error) {
		return value
	}
}

const parseBoolValue = (value, defaultValue = false) => {
	if (!value) {
		return defaultValue
	}

	return value === 'true' ? true : false
}

const parseHTMLElement = (id) => (id && typeof window !== 'undefined' ? document.getElementById(id) : null)

export function getBackground(node, props) {
	const { style } = props
	const { desktop_image, mobile_image } = node.attribs['data-background-images']
		? JSON.parse(
				unescape(
					node.attribs['data-background-images']
						// Remove excessive backslashes until https://filoblu.atlassian.net/browse/BEEH-101 will be resolved
						.replace(/\\"/g, '"')
				)
		  )
		: {}
	const desktopImage = normalizeMagentoUrl(node.attribs['data-background-image'] || desktop_image)
	const mobileImage = normalizeMagentoUrl(node.attribs['data-background-mobile-image'] || mobile_image)

	//Mobile
	let videoMobile = node.attribs['data-background-mobile-video-path'] || node.attribs['data-mobile-video-source']

	// Desktop
	const video = node.attribs['data-background-video-path'] || node.attribs['data-video-source']

	if (!videoMobile) {
		videoMobile = video
	}

	return {
		desktopImage,
		mobileImage,
		objectFit: style.backgroundSize === 'auto' ? 'none' : style.backgroundSize,
		objectPosition: style.backgroundPosition,
		backgroundAttachment: style.backgroundAttachment,
		backgroundRepeat: style.backgroundRepeat || 'repeat',
		backgroundColor: style.backgroundColor,
		backgroundType: node.attribs['data-background-type'],
		videoBackground: {
			mobileUrl: videoMobile ? (isFullURL(videoMobile) ? videoMobile : mediaUrl(videoMobile)) : null,
			desktopUrl: video ? (isFullURL(video) ? video : mediaUrl(video)) : null,
		},
	}
}

const alignmentToFlex = {
	top: 'flex-start',
	middle: 'center',
	bottom: 'flex-end',
}

export function getVerticalAlignment(node) {
	let alignItems = null
	if (node.attribs['data-justify-content']) {
		alignItems = node.attribs['data-justify-content']
	}

	return {
		alignItems,
	}
}

export function verticalAlignmentToFlex(alignment) {
	return alignmentToFlex[alignment]
}

export function flexToVerticalAlignment(flex) {
	const flexToAlignment = Object.assign({}, ...Object.entries(alignmentToFlex).map(([a, b]) => ({ [b]: a })))
	return flexToAlignment[flex]
}

export function getAdvanced(node) {
	const advanced = {
		...getPadding(node),
		...getMargin(node),
		...getTextAlign(node),
		...getClassName(node),
		...getIsHidden(node),
		...getCustomStyles(node),
		...getAboveTheFold(node),
	}

	return {
		...advanced,
		sx: advancedToSx(advanced),
		cssProp: advanced.customStyles ? css(advanced.customStyles) : undefined,
	}
}

export function getPadding(node) {
	const { style } = attributesToProps(node.attribs)
	const paddingMobile = node.attribs['data-mobile-padding']

	return {
		paddingMobile,
		padding: style.padding,
		paddingTop: style.paddingTop,
		paddingRight: style.paddingRight,
		paddingBottom: style.paddingBottom,
		paddingLeft: style.paddingLeft,
	}
}

export function getMargin(node) {
	const { style } = attributesToProps(node.attribs)

	const marginMobile = node.attribs['data-mobile-margin']
	return {
		marginMobile,
		margin: style.margin,
		marginTop: style.marginTop,
		marginRight: style.marginRight,
		marginBottom: style.marginBottom,
		marginLeft: style.marginLeft,
	}
}

export function getBorder(node) {
	const { style } = attributesToProps(node.attribs)
	return {
		border: style.borderStyle,
		borderColor: style.borderColor,
		borderWidth: style.borderWidth,
		borderRadius: style.borderRadius,
	}
}

export function getTextAlign(node) {
	const { style } = attributesToProps(node.attribs)
	return {
		textAlign: style.textAlign,
	}
}

export function getClassName(node) {
	return {
		className: node.attribs.class,
	}
}

export function getMinHeight(node) {
	const minHeight = node.attribs['data-min-height'] ? `${node.attribs['data-min-height']}px` : null
	const minHeightMobile = node.attribs['data-mobile-min-height']
		? `${node.attribs['data-mobile-min-height']}px`
		: minHeight

	return {
		minHeight: { xs: minHeightMobile, md: minHeight },
	}
}

export function getIsHidden(node) {
	const { style } = attributesToProps(node.attribs)
	return {
		isHidden: style.display === 'none',
	}
}

export function getCustomStyles(node) {
	return {
		customStyles: node.attribs['data-custom-style']
			? normalizeCarriageReturns(node.attribs['data-custom-style'])
			: null,
	}
}

export function getImageLoadingPriority(node) {
	return {
		// https://nextjs.org/docs/pages/api-reference/components/image#priority
		...(node.attribs['data-load-with-priority'] === 'true'
			? { loading: IMAGE_LOADING_EAGER, priority: true }
			: {
					// defaults
					loading: IMAGE_DEFAULT_LOADING,
					priority: IMAGE_DEFAULT_PRIORITY,
			  }),
	}
}

export function cssToJSXStyle(style) {
	const toCamelCase = (str) => str.replace(/-(.)/g, (_, p) => p.toUpperCase())
	const result = {}
	style.split(';').forEach((el) => {
		const [prop, value] = el.split(':')
		if (prop) {
			result[toCamelCase(prop.trim())] = value.trim()
		}
	})

	return result
}

export function getMediaQueries(node) {
	const response = []
	const dataset = Object.keys(node.attribs).filter((attr) => /^data-.*/.test(attr))

	const medias = dataset.filter((key) => key.match(/media-/)).map((key) => node.attribs[key])

	const styles = dataset.filter((key) => key.match(/mediaStyle/)).map((key) => node.attribs[key])

	medias.forEach((media, i) => {
		response.push({
			media,
			style: cssToJSXStyle(styles[i]),
		})
	})

	return { mediaQueries: response }
}

export function advancedToSx(props) {
	const {
		marginMobile,
		margin,
		marginTop,
		marginRight,
		marginBottom,
		marginLeft,
		paddingMobile,
		padding,
		paddingTop,
		paddingRight,
		paddingBottom,
		paddingLeft,
	} = props

	return {
		margin: { xs: marginMobile, md: margin },
		padding: { xs: paddingMobile, md: padding },
		marginTop: { md: marginTop },
		marginRight: { md: marginRight },
		marginBottom: { md: marginBottom },
		marginLeft: { md: marginLeft },
		paddingTop: { md: paddingTop },
		paddingRight: { md: paddingRight },
		paddingBottom: { md: paddingBottom },
		paddingLeft: { md: paddingLeft },
	}
}

export function normalizeMagentoUrl(url) {
	return url?.replace?.(/\{\{media url="?/, '')?.replace?.(/"?\}\}/, '')
}

export function getSliderSettings(node) {
	const autoplayDelay = parseInt(node.attribs['data-autoplay-speed'])

	const autoplay =
		node.attribs['data-autoplay'] === 'true' ? (!isNaN(autoplayDelay) ? { delay: autoplayDelay } : true) : false

	/**
	 * BREAKPOINTS INPUT EXAMPLE
	 *
	 *  {
	 *  	"320": {
	 *  		"slidesPerView": 2,
	 *  		"spaceBetween": 20,
	 *  		"loop": false,
	 *  	},
	 *  }
	 */

	const sliderSettings = {
		breakpoints: node.attribs['data-breakpoints'] ? JSON.parse(node.attribs['data-breakpoints']) : {},
		autoplay, // Not available on breakpoints
		pagination: node.attribs['data-pagination-type'] !== 'none', // Not available on breakpoints
		// fade: node.attribs['data-fade'] === 'true',
		loop: node.attribs['data-infinite-loop'] === 'true',
		navigation: node.attribs['data-show-arrows'] === 'true',
		grabCursor: true,
		// showDots: node.attribs['data-show-dots'] === 'true',
		direction:
			!node.attribs['data-direction'] || node.attribs['data-direction'] === 'none' //when creating a slider from scratch and saving without any edit, direction value is "none"
				? 'horizontal'
				: node.attribs['data-direction'], // Not available on breakpoints
		speed: node.attribs['data-speed'] ? parseInt(node.attribs['data-speed']) : undefined,
		effect: node.attribs['data-effect'], // Not available on breakpoints
		spaceBetween: node.attribs['data-space-between'] ? parseInt(node.attribs['data-space-between']) : undefined,
		slidesPerView: node.attribs['data-slides-per-view'] ? parseFloat(node.attribs['data-slides-per-view']) : undefined,
		slidesPerGroup: node.attribs['data-slides-per-group'] ? parseInt(node.attribs['data-slides-per-group']) : undefined,
		centeredSlides: node.attribs['data-centered-slide'] === 'true',
		freeMode: node.attribs['data-free-mode'] === 'true',
	}
	return { sliderSettings }
}

export function getProductsCorrelation(node) {
	let sortBy = ''
	let sortByDirection = ''

	switch (node.attribs['data-sort-by']) {
		case 'position':
			sortBy = ''
			sortByDirection = 'ASC'
			break
		case 'date_newest_top':
			break
		case 'date_oldest_top':
			break
		case 'name_ascending':
			sortBy = 'name'
			sortByDirection = 'ASC'
			break
		case 'name_descending':
			sortBy = 'name'
			sortByDirection = 'DESC'
			break
		case 'sku_ascending':
			sortBy = 'sku'
			sortByDirection = 'ASC'
			break
		case 'sku_descending':
			sortBy = 'sku'
			sortByDirection = 'DESC'
			break
		case 'low_stock_first':
			break
		case 'high_stock_first':
			break
		case 'price_high_to_low':
			break
		case 'price_low_to_high':
			break
	}

	return {
		productsCorrelation: {
			entityType: node.attribs['data-select-product-by'],
			limit: node.attribs['data-display-products-limit'] ? parseInt(node.attribs['data-display-products-limit']) : 10, // not undefined to avoid too many API calls
			skus: node.attribs['data-selected-sku'] ? node.attribs['data-selected-sku'].split(',') : [],
			categoryId: parseInt(node.attribs['data-selected-category-ids']),
			sortBy,
			sortByDirection,
		},
	}
}

export function getSlideSettings(node) {
	return {
		slideName: node.attribs['data-slide-name'],
	}
}

export const getImage = (node) => {
	//Mobile
	let imageMobile = node.attribs['data-banner-mobile-image'] || node.attribs['data-banner-mobile-image-url']
	let imageMobileDimensions = {
		originalWidth: node.attribs['data-banner-mobile-image-width']
			? parseInt(node.attribs['data-banner-mobile-image-width'])
			: null,
		originalHeight: node.attribs['data-banner-mobile-image-height']
			? parseInt(node.attribs['data-banner-mobile-image-height'])
			: null,
	}

	// Desktop
	const image = node.attribs['data-banner-image'] || node.attribs['data-banner-image-url']
	const imageDimensions = {
		originalWidth: node.attribs['data-banner-image-width'] ? parseInt(node.attribs['data-banner-image-width']) : null,
		originalHeight: node.attribs['data-banner-image-height']
			? parseInt(node.attribs['data-banner-image-height'])
			: null,
	}

	if (!imageMobile) {
		imageMobile = image
		imageMobileDimensions = imageDimensions
	}

	const imageMobileObj = {
		src: imageMobile,
		originalWidth: imageMobileDimensions.originalWidth,
		originalHeight: imageMobileDimensions.originalHeight,
	}
	const imageDesktopObj = {
		src: image,
		originalWidth: imageDimensions.originalWidth,
		originalHeight: imageDimensions.originalHeight,
	}

	return {
		image: {
			mobile: imageMobileObj,
			desktop: imageDesktopObj,
		},
		imageConfig: getImageLoadingPriority(node),
	}
}

export const getCoverImage = (node) => {
	//Mobile
	let imageMobile =
		node.attribs['data-banner-video-cover-mobile-image'] || node.attribs['data-banner-video-cover-mobile-image-url']
	let imageMobileDimensions = {
		originalWidth: node.attribs['data-banner-video-cover-mobile-image-width']
			? parseInt(node.attribs['data-banner-video-cover-mobile-image-width'])
			: undefined,
		originalHeight: node.attribs['data-banner-video-cover-mobile-image-height']
			? parseInt(node.attribs['data-banner-video-cover-mobile-image-height'])
			: undefined,
	}

	// Desktop
	const image = node.attribs['data-banner-video-cover-image'] || node.attribs['data-banner-video-cover-image-url']
	const imageDimensions = {
		originalWidth: node.attribs['data-banner-video-cover-image-width']
			? parseInt(node.attribs['data-banner-video-cover-image-width'])
			: undefined,
		originalHeight: node.attribs['data-banner-video-cover-image-height']
			? parseInt(node.attribs['data-banner-video-cover-image-height'])
			: undefined,
	}

	if (!imageMobile) {
		imageMobile = image
	}

	if (!imageMobileDimensions.originalWidth && !imageMobileDimensions.originalHeight) {
		imageMobileDimensions = imageDimensions
	}

	const imageMobileObj = {
		src: imageMobile,
		originalWidth: imageMobileDimensions.originalWidth,
		originalHeight: imageMobileDimensions.originalHeight,
	}
	const imageDesktopObj = {
		src: image,
		originalWidth: imageDimensions.originalWidth,
		originalHeight: imageDimensions.originalHeight,
	}

	return {
		coverImage: {
			mobile: imageMobileObj,
			desktop: imageDesktopObj,
		},
		imageConfig: getImageLoadingPriority(node),
	}
}
export const getVideo = (node) => {
	//Mobile
	let videoMobile = node.attribs['data-banner-video-mobile-path'] || node.attribs['data-banner-video-mobile-url']

	// Desktop
	const video = node.attribs['data-banner-video-path'] || node.attribs['data-banner-video-url']

	if (!videoMobile) {
		videoMobile = video
	}
	const { coverImage } = getCoverImage(node)
	const coverImageMobileSrc = coverImage.mobile?.src
	const coverImageDesktopSrc = coverImage.desktop?.src

	return {
		video: {
			mobile: {
				light: coverImageMobileSrc ?? null,
				url: videoMobile ? (isFullURL(videoMobile) ? videoMobile : mediaUrl(videoMobile)) : null,
				originalWidth: coverImage.mobile?.originalWidth,
				originalHeight: coverImage.mobile?.originalHeight,
			},
			desktop: {
				light: coverImageDesktopSrc ?? null,
				url: video ? (isFullURL(video) ? video : mediaUrl(video)) : null,
				originalWidth: coverImage.desktop?.originalWidth,
				originalHeight: coverImage.desktop?.originalHeight,
			},
		},
		videoConfig: {
			autoplay: node.attribs['data-banner-video-autoplay'] === 'true',
			loop: node.attribs['data-banner-video-loop'] === 'true',
			stopOnEveryOutView: node.attribs['data-content-type'] === 'slide',
			startOnEveryInView: node.attribs['data-content-type'] === 'slide',
		},
	}
}

export function getCtaStyle(attrib) {
	let color = undefined

	switch (attrib) {
		case 'pagebuilder-button-primary':
			color = 'primary'
			break
		case 'pagebuilder-button-secondary':
			color = 'secondary'
	}

	const variant = attrib === 'pagebuilder-button-link' ? 'text' : undefined

	return { color, variant }
}

export function getCtaLink(attrib) {
	const link = attrib ? (isNaN(attrib) ? attrib : parseInt(attrib)) : undefined

	return { link }
}

export function getContent(node) {
	// eslint-disable-next-line regexp/no-unused-capturing-group
	const titleIsHtml = /<([A-Za-z][A-Za-z\d]*)\b[^>]*>(.*?)<\/\1>/.test(node.attribs['data-title'])

	return {
		content: node.attribs['data-banner-content'],
		content2: node.attribs['data-banner-content-2'],
		title: node.attribs['data-title'],
		titleIsHtml,
		titleTag: titleIsHtml ? 'div' : node.attribs['data-title-element-type'] || 'p',
		titleColor: node.attribs['data-title-color'],
		titleColorMobile: node.attribs['data-mobile-title-color'],
		subtitle: node.attribs['data-subtitle'],
		subtitleTag: node.attribs['data-subtitle-element-type'] ? node.attribs['data-subtitle-element-type'] : 'p',
		subtitleColor: node.attribs['data-subtitle-color'],
		subtitleColorMobile: node.attribs['data-mobile-subtitle-color'],
		ctas: [
			// CTA 1
			{
				...getCtaStyle(node.attribs['data-button-type']),
				text: node.attribs['data-button-text'],
				linkType: node.attribs['data-link-type'],
				...getCtaLink(node.attribs['data-link-href']),
				openInNewTab: node.attribs['data-link-target'],
			},
			// CTA 2
			{
				...getCtaStyle(node.attribs['data-button-type-2']),
				text: node.attribs['data-button-text-2'],
				linkType: node.attribs['data-link-type-2'],
				...getCtaLink(node.attribs['data-link-href-2']),
				openInNewTab: node.attribs['data-link-target-2'],
			},
			// CTA 3
			{
				...getCtaStyle(node.attribs['data-button-type-3']),
				text: node.attribs['data-button-text-3'],
				linkType: node.attribs['data-link-type-3'],
				...getCtaLink(node.attribs['data-link-href-3']),
				openInNewTab: node.attribs['data-link-target-3'],
			},
		].filter(({ link }) => !!link),
		...getImage(node),
	}
}

export const getMasterDataComponent = (config, type, template) => {
	const variant = [type, template].join('-')
	const defaultVariant = ['default', template].join('-')

	return config[variant] ?? config[defaultVariant] ?? config['default']
}

export const getLeadFormComponent = (config, type, template) => {
	const variant = [type, template].join('-')
	const defaultVariant = [type, 'default'].join('-')

	return config[variant] ?? config[defaultVariant]
}

const getDisabledReactScrollParallax = (node) => {
	const forceDisabled =
		!node.attribs['data-rsp-speed'] &&
		!node.attribs['data-rsp-translate-x'] &&
		!node.attribs['data-rsp-translate-y'] &&
		!node.attribs['data-rsp-rotate'] &&
		!node.attribs['data-rsp-rotate-x'] &&
		!node.attribs['data-rsp-rotate-y'] &&
		!node.attribs['data-rsp-rotate-z'] &&
		!node.attribs['data-rsp-scale'] &&
		!node.attribs['data-rsp-scale-x'] &&
		!node.attribs['data-rsp-scale-y'] &&
		!node.attribs['data-rsp-scale-z'] &&
		!node.attribs['data-rsp-opacity']

	return forceDisabled || parseBoolValue(node.attribs['data-rsp-disabled'])
}

export const getReactScrollParallaxConfig = (node) => {
	return {
		speed: parseInt(node.attribs['data-rsp-speed'], 0) || null,
		easing: parseJsonValue(node.attribs['data-rsp-easing']),
		rootMargin: parseJsonValue(node.attribs['data-rsp-root-margin']),
		disabled: getDisabledReactScrollParallax(node),
		shouldAlwaysCompleteAnimation: parseBoolValue(node.attribs['data-rsp-should-always-complete-animation']),
		shouldDisableScalingTranslations: parseBoolValue(node.attribs['data-rsp-should-disable-scaling-translations']),
		startScroll: parseInt(node.attribs['data-rsp-start-scroll'], 0) || null,
		endScroll: parseInt(node.attribs['data-rsp-end-scroll'], 0) || null,
		targetElement: parseHTMLElement(node.attribs['data-rsp-target-element']),
		translateX: parseJsonValue(node.attribs['data-rsp-translate-x']),
		translateY: parseJsonValue(node.attribs['data-rsp-translate-y']),
		rotate: parseJsonValue(node.attribs['data-rsp-rotate']),
		rotateX: parseJsonValue(node.attribs['data-rsp-rotate-x']),
		rotateY: parseJsonValue(node.attribs['data-rsp-rotate-y']),
		rotateZ: parseJsonValue(node.attribs['data-rsp-rotate-z']),
		scale: parseJsonValue(node.attribs['data-rsp-scale']),
		scaleX: parseJsonValue(node.attribs['data-rsp-scale-x']),
		scaleY: parseJsonValue(node.attribs['data-rsp-scale-y']),
		scaleZ: parseJsonValue(node.attribs['data-rsp-scale-z']),
		opacity: parseJsonValue(node.attribs['data-rsp-opacity']),
		onProgressChange: node.attribs['data-rsp-on-progress-change']
			? eval(node.attribs['data-rsp-on-progress-change'])
			: null,
		onChange: node.attribs['data-rsp-on-change'] ? eval(node.attribs['data-rsp-on-change']) : null,
		onEnter: node.attribs['data-rsp-on-enter'] ? eval(node.attribs['data-rsp-on-enter']) : null,
		onExit: node.attribs['data-rsp-on-exit'] ? eval(node.attribs['data-rsp-on-exit']) : null,
	}
}

export const getAboveTheFold = (node) => {
	const aboveTheFold = parseBoolValue(node?.attribs['data-above-fold'])

	return {
		aboveTheFold,
	}
}
